import React, { createContext, useState, useEffect, useCallback } from "react";

const AUTH_STORAGE_KEYS = [
  "authData",
  "userProfile",
  "userToken",
  "isAuthUser",
  "isLoadedUserProfile",

  "ovirtData",
  "ovirtSSOToken",
  "ovirtWebadminUrl",
  "ovirtDataLastFetch",

  "storageData",
  "isLoadedStorageData",
  "storageDataLastFetch",

  "okdData",
  "isLoadedOkdData",
  "okdDataLastFetch",
];

const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
};
const getIntegrationsStatus = async () => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}` +
      "/ti/api/integrations/status/",
    {
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error(
      `Integrations status failed (${response.status})`
    );
  }

  return response.json();
};
// Inicjalny stan autoryzacji
const initialAuthState = {
  isOvirtSessionExpired: false,
  isAuthUser: false,
  isObtainUserToken: false,
  isLoadedUserProfile: false,

  isOkdAvailable: null,
  isOvirtAvailable: null,
  areIntegrationsReady: false,
  authData: [],
  userProfile: {},
  userToken: "",
  // isAuthUser: false,
  // isObtainUserToken: false,
  // isLoadedUserProfile: false,
  // authData: [],
  // userProfile: {},
  // userToken: "",
  // 🔥 oVirt (cache jak React Query)
  ovirtData: null,
  ovirtSSOToken: "",
  ovirtWebadminUrl: "",
  ovirtDataLastFetch: null,
  // 🔥 Ceph (cache jak React Query)
  storageData: null,
  isLoadedStorageData: false,
  storageDataLastFetch: null,
  // 🔥 OKD NOWE (cache jak React Query)
  okdData: null,
  isLoadedOkdData: false,
  okdDataLastFetch: null,

  systemInfo: null,
  isLoadedSystemInfo: false,
};

const isDev = process.env.REACT_APP_STAGE === "dev";

// export const AuthContext = createContext({
//   state: initialAuthState,
//   setContext: () => {},
//   logout: () => {},
// });
export const AuthContext = createContext({
  state: initialAuthState,
  setContext: () => {},
  logout: () => {},
  loadSystemInfo: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(initialAuthState);

  const log = (...args) => {
    if (isDev) console.log("[AuthContext]", ...args);
  };

  const setContext = useCallback((updates = {}) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };

      // Zapisz w localStorage tylko jeśli jest pełny profil
      if ("authData" in updates)
        localStorage.setItem("authData", JSON.stringify(updates.authData));
      if ("userProfile" in updates)
        localStorage.setItem(
          "userProfile",
          JSON.stringify(updates.userProfile),
        );
      if ("userToken" in updates)
        localStorage.setItem("userToken", JSON.stringify(updates.userToken));
      if ("isAuthUser" in updates)
        localStorage.setItem(
          "isAuthUser",
          updates.isAuthUser ? "true" : "false",
        );
      if ("isLoadedUserProfile" in updates)
        localStorage.setItem(
          "isLoadedUserProfile",
          updates.isLoadedUserProfile ? "true" : "false",
        );

      // 🔥 oVirt
      if ("ovirtData" in updates && updates.ovirtData !== undefined) {
        localStorage.setItem("ovirtData", JSON.stringify(updates.ovirtData));
      }
      if ("ovirtSSOToken" in updates)
        localStorage.setItem("ovirtSSOToken", updates.ovirtSSOToken || "");
      if ("ovirtWebadminUrl" in updates)
        localStorage.setItem(
          "ovirtWebadminUrl",
          updates.ovirtWebadminUrl || "",
        );
      if ("ovirtDataLastFetch" in updates)
        localStorage.setItem(
          "ovirtDataLastFetch",
          updates.ovirtDataLastFetch || ""
        );

      // 🔥 Ceph CACHE
      if ("storageData" in updates)
        localStorage.setItem(
          "storageData",
          JSON.stringify(updates.storageData),
        );
      if ("isLoadedStorageData" in updates)
        localStorage.setItem(
          "isLoadedStorageData",
          updates.isLoadedStorageData ? "true" : "false",
        );
      if ("storageDataLastFetch" in updates)
        localStorage.setItem(
          "storageDataLastFetch",
          updates.storageDataLastFetch || "",
        );

      // 🔥 OKD CACHE
      if ("okdData" in updates)
        localStorage.setItem("okdData", JSON.stringify(updates.okdData));
      if ("isLoadedOkdData" in updates)
        localStorage.setItem(
          "isLoadedOkdData",
          updates.isLoadedOkdData ? "true" : "false",
        );
      if ("okdDataLastFetch" in updates)
        localStorage.setItem(
          "okdDataLastFetch",
          updates.okdDataLastFetch || "",
        );

      return newState;
    });
  }, []);

  useEffect(() => {
    try {
      // Wczytaj stan z localStorage
      const storedAuthData = JSON.parse(localStorage.getItem("authData")) || [];
      const storedUserProfile =
        JSON.parse(localStorage.getItem("userProfile")) || {};
      const storedUserToken =
        JSON.parse(localStorage.getItem("userToken")) || "";
      const storedIsAuthUser = localStorage.getItem("isAuthUser") === "true";
      const storedIsLoadedUserProfile =
        localStorage.getItem("isLoadedUserProfile") === "true";
      // oVirt
      const storedOvirtData =
        JSON.parse(localStorage.getItem("ovirtData")) || null;
      const storedOvirtSSOToken = localStorage.getItem("ovirtSSOToken") || "";
      const storedOvirtWebadminUrl =
        localStorage.getItem("ovirtWebadminUrl") || "";
      const storedOvirtDataLastFetch =
        Number(localStorage.getItem("ovirtDataLastFetch")) || null;
      // CEPH
      const storedStorageData =
        JSON.parse(localStorage.getItem("storageData")) || null;
      const storedIsLoadedStorageData =
        localStorage.getItem("isLoadedStorageData") === "true";
      const storedStorageDataLastFetch =
        Number(localStorage.getItem("storageDataLastFetch")) || null;
      // OKD
      const storedOkdData = JSON.parse(localStorage.getItem("okdData")) || null;
      const storedIsLoadedOkdData =
        localStorage.getItem("isLoadedOkdData") === "true";
      const storedOkdDataLastFetch =
        Number(localStorage.getItem("okdDataLastFetch")) || null;

      setState((prev) => ({
        ...prev,
        authData: storedAuthData,
        userProfile: storedUserProfile,
        userToken: storedUserToken,
        isAuthUser: storedIsAuthUser,
        isLoadedUserProfile: storedIsLoadedUserProfile,
        isObtainUserToken: !!storedUserToken,
        // 🔥 oVirt
        ovirtData: storedOvirtData,
        ovirtSSOToken: storedOvirtSSOToken,
        ovirtWebadminUrl: storedOvirtWebadminUrl,
        ovirtDataLastFetch: storedOvirtDataLastFetch,
        // 🔥 Ceph
        storageData: storedStorageData,
        isLoadedStorageData: storedIsLoadedStorageData,
        storageDataLastFetch: storedStorageDataLastFetch,
        // 🔥 OKD
        okdData: storedOkdData,
        isLoadedOkdData: storedIsLoadedOkdData,
        okdDataLastFetch: storedOkdDataLastFetch,
      }));
    } catch (e) {
      log("Błąd przy wczytywaniu AuthContext z localStorage:", e);
    }

    // Sprawdź, czy sesja backendowa istnieje
    fetch(`${process.env.REACT_APP_API_URL}/ti/api/profile/`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Sesja wygasła");
        return res.json();
      })
      .then((data) => {
        // if (!data.authenticated) {
        //   setContext({
        //     isAuthUser: false,
        //     isLoadedUserProfile: true,
        //     isOkdAvailable: null,
        //     isOvirtAvailable: null,
        //     areIntegrationsReady: false,
        //     isOvirtSessionExpired: false,
        //     userProfile: {},

        //     ...(data.ovirt_data && {
        //       ovirtData: data.ovirt_data,
        //     }),

        //     ...(data.ovirt_sso_token && {
        //       ovirtSSOToken:
        //         data.ovirt_sso_token,
        //     }),

        //     ...(data.ovirt_webadmin_url && {
        //       ovirtWebadminUrl:
        //         data.ovirt_webadmin_url,
        //     }),
        //   });
        //   return;
        // }
        if (!data.authenticated) {
          setContext({
            isAuthUser: false,
            isLoadedUserProfile: true,

            isOkdAvailable: null,
            isOvirtAvailable: null,
            areIntegrationsReady: false,
            isOvirtSessionExpired: false,

            userProfile: {},

            ovirtData: null,
            ovirtSSOToken: "",
            ovirtWebadminUrl: "",
            ovirtDataLastFetch: null,

            okdData: null,
            isLoadedOkdData: false,
            okdDataLastFetch: null,

            storageData: null,
            isLoadedStorageData: false,
            storageDataLastFetch: null,
          });

          return;
        }
        // setContext({
        //   isAuthUser: true,
        //   isLoadedUserProfile: true,
        //   isOkdAvailable: null,
        //   isOvirtAvailable: null,
        //   areIntegrationsReady: false,
        //   isOvirtSessionExpired: false,
        //   userProfile: data.user,

        //   ...(data.ovirt_data && {
        //     ovirtData: data.ovirt_data,
        //     ovirtDataLastFetch: Date.now(),
        //   }),

        //   ...(data.ovirt_sso_token && {
        //     ovirtSSOToken: data.ovirt_sso_token,
        //   }),

        //   ...(data.ovirt_webadmin_url && {
        //     ovirtWebadminUrl:
        //       data.ovirt_webadmin_url,
        //   }),
        // });
        setContext({
          isAuthUser: true,
          isLoadedUserProfile: true,
          isOkdAvailable: null,
          isOvirtAvailable: null,
          areIntegrationsReady: false,
          isOvirtSessionExpired: false,
          userProfile: data.user,

          ...(data.ovirt_sso_token && {
            ovirtSSOToken:
              data.ovirt_sso_token,
          }),

          ...(data.ovirt_webadmin_url && {
            ovirtWebadminUrl:
              data.ovirt_webadmin_url,
          }),
        });

        void getIntegrationsStatus()
          .then((integrations) => {
            setContext({
              isOkdAvailable:
                integrations.okd_available === true,

              isOvirtAvailable:
                integrations.ovirt_available === true,

              areIntegrationsReady: true,

              ...(integrations.ovirt_available === true && {
                isOvirtSessionExpired: false,
              }),

              ...(integrations.ovirt_sso_token && {
                ovirtSSOToken:
                  integrations.ovirt_sso_token,
              }),

              ...(integrations.ovirt_webadmin_url && {
                ovirtWebadminUrl:
                  integrations.ovirt_webadmin_url,
              }),
            });
          })
          .catch((error) => {
            console.error(
              "[AuthContext] Integrations status error:",
              error
            );

            setContext({
              isOkdAvailable: false,
              isOvirtAvailable: false,
              areIntegrationsReady: true,
            });
          });

        void loadSystemInfo();
    }).catch(() => {
        setState({ ...initialAuthState });
        clearAuthStorage();
      });
  }, [setContext]);

  const logout = useCallback(async () => {
    try {
      const token = document.cookie
        .split("; ")
        .find((cookie) => cookie.startsWith("csrftoken="))
        ?.split("=")[1];

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ti/api/logout/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": token,
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(`Logout failed (${response.status})`);
      }
    } catch (err) {
      console.error("[AuthContext] logout error:", err);
    }

    setState(initialAuthState);
    clearAuthStorage();
  }, []);

  const loadSystemInfo = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ti/api/system-info/`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(
          `System info failed (${response.status})`
        );
      }

      const data = await response.json();

      setContext({
        systemInfo: data,
        isLoadedSystemInfo: true,
      });

    } catch (err) {
      log(
        "System info error:",
        err
      );
    }
  }, [setContext]);
  return (
    <AuthContext.Provider
      value={{
        state,
        setContext,
        logout,
        loadSystemInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
